import type { ReactNode} from 'react';
import { createContext, useContext, useMemo } from 'react';

import type { IArticlePriceInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { logToConsole } from '~/logging/consoleLogger';
import type { CacheMap} from '~/shell/hooks/useBatchFetch';
import { useBatchFetch } from '~/shell/hooks/useBatchFetch';
import { clientFetch } from '~/common/utils/clientFetch.client';

async function fetchArticlePrices(ids: number[]): Promise<Record<number, IArticlePriceInfoDtoData | null>> {
  const mainArticleIds = ids.join(',');

  try {
    const priceinfoResponse = await clientFetch(`/infra/resources/articlePriceInfos?mainArticleIds=${mainArticleIds}`);
    const priceInfos = (await priceinfoResponse.json()) as IArticlePriceInfoDtoData[];

    const result: Record<number, IArticlePriceInfoDtoData> = {};
    priceInfos.forEach(item => {
      result[item.mainArticleId] = item;
    });

    return result;
  } catch (e) {
    logToConsole({ loggerName: 'price-context', logLevel: 'ERROR', message: 'Error fetching price info', args: [e] });
    throw e;
  }
}

type PriceMap = CacheMap<IArticlePriceInfoDtoData>;

interface PriceContextValue {
  fetchPrice: (mainArticleId: number[]) => Promise<IArticlePriceInfoDtoData[] | null>;
  priceMap: PriceMap;
}

const PriceContext = createContext<PriceContextValue | null>(null);

interface PriceProviderProps {
  children: ReactNode;
  flushTimeout: number;
  flushQueueSize: number;
  maxCacheItems: number;
}

export const PriceProvider = ({ children, flushTimeout, flushQueueSize, maxCacheItems }: PriceProviderProps) => {
  const { fetch: fetchPrice, cacheMap: priceMap } = useBatchFetch<IArticlePriceInfoDtoData>({
    fetchFn: fetchArticlePrices,
    flushTimeout,
    flushQueueSize,
    maxCacheItems
  });

  const value: PriceContextValue = useMemo(
    () => ({
      fetchPrice,
      priceMap
    }),
    [fetchPrice, priceMap]
  );

  return <PriceContext.Provider value={value}>{children}</PriceContext.Provider>;
};

export function usePriceContext() {
  const context = useContext(PriceContext);
  if (!context) {
    throw new Error('usePriceContext must be used within a PriceProvider');
  }
  return context;
}
